import React, { useEffect, useState } from "react";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatCr } from "@/lib/format";
import { Plus, Search, AlertTriangle } from "lucide-react";
import { SourceBadge } from "@/components/v3/SourceBadge";
import { STAGE_META } from "@/components/v3/CockpitLive";
import { LoadingState, EmptyState } from "@/components/v3/States";
import { useAuth } from "@/context/AuthContext";

const TILES = [
  { key: "draft", label: "Draft", stages: ["lead"] },
  { key: "submitted", label: "Submitted", stages: ["submitted"] },
  { key: "info", label: "Info Required", stages: ["info_required"] },
  { key: "credit", label: "Credit / Risk", stages: ["review"] },
  { key: "committee", label: "Committee", stages: ["committee"] },
  { key: "approved", label: "Approved", stages: ["sanctioned", "approved"] },
  { key: "disbursed", label: "Disbursed", stages: ["disbursed"] },
  { key: "declined", label: "Declined", stages: ["declined"] },
];

function LegacyLookup() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    api.get("/v3/dsa/legacy-activity", { params: { q } })
      .then((r) => setRes(r.data))
      .catch((e) => setRes({ found: false, message: formatApiErrorDetail(e.response?.data?.detail) || e.message }))
      .finally(() => setBusy(false));
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="legacy-dsa-card">
      <div className="flex items-center gap-2 mb-1">
        <span className="font-semibold text-slate-900 text-sm">Historical DSA activity</span>
        <SourceBadge source="HISTORICAL" />
      </div>
      <div className="text-[11px] text-slate-400 mb-3">Look up a past DSA and their sourced applications — read-only.</div>
      <div className="flex gap-2 mb-3">
        <input data-testid="legacy-dsa-input" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="DSA name or email…" className="flex-1 border border-slate-300 rounded px-3 py-1.5 text-sm" />
        <button data-testid="legacy-dsa-search-btn" onClick={search}
          className="bg-[#111111] text-white rounded px-3 text-sm font-semibold inline-flex items-center gap-1"><Search size={13} /> Find</button>
      </div>
      {busy && <LoadingState label="Searching legacy DSAs…" />}
      {!busy && res && !res.found && <div className="text-[12px] text-slate-400" data-testid="legacy-dsa-empty">{res.message || "No historical DSA matches"}</div>}
      {!busy && res?.found && (
        <div data-testid="legacy-dsa-result">
          <div className="text-sm font-semibold text-slate-800">{res.dsa.name} <span className="text-[11px] text-slate-400">{res.dsa.email}</span></div>
          <div className="text-[11px] text-slate-400 mb-2">{res.total_applications} historical applications{res.dsa.rank ? ` · rank ${res.dsa.rank}` : ""}</div>
          <div className="max-h-56 overflow-auto divide-y divide-slate-100">
            {res.applications.map((a) => (
              <div key={a.id} className="py-1.5 flex items-center gap-2 text-[12px]" data-testid="legacy-dsa-app-row">
                <span className="flex-1 truncate text-slate-700 font-semibold">{a.company}</span>
                <span className="text-slate-400 tabular-nums">{a.amount ? formatCr(a.amount) : "—"}</span>
                <span className="text-slate-500 uppercase text-[10px] font-bold" style={{ color: STAGE_META[a.stage]?.color }}>{STAGE_META[a.stage]?.label || a.stage}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DsaConsole() {
  const [version] = useLiveRefresh();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [score, setScore] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/v3/my-applications").then((r) => setApps(r.data.applications)).catch(() => setApps([]));
    api.get("/v3/dsa/quality-score").then((r) => setScore(r.data)).catch(() => {});
  }, [version]);

  const count = (stages) => (apps || []).filter((a) => stages.includes(a.stage)).length;
  const visible = (apps || []).filter((a) => !filter || TILES.find((t) => t.key === filter)?.stages.includes(a.stage));
  const infoApps = (apps || []).filter((a) => a.stage === "info_required");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1300px]" data-testid="dsa-console-page">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="ceo-label !text-[#b3122a]">Originate · Build 3</div>
          <h1 className="ceo-fig text-2xl sm:text-3xl text-[#111111] mt-1">DSA Console</h1>
          <p className="text-[13px] text-slate-500 mt-1">Hi {user?.name} — your V3 test applications and historical reference activity.</p>
        </div>
        <button data-testid="console-new-application-btn" onClick={() => navigate("/apply")}
          className="inline-flex items-center gap-1.5 bg-[#b3122a] text-white text-sm font-semibold rounded px-3.5 py-2 hover:bg-[#8f0e21]">
          <Plus size={15} /> New application
        </button>
      </header>

      {infoApps.length > 0 && (
        <div className="mb-5 space-y-2" data-testid="info-required-alerts">
          {infoApps.map((a) => (
            <button key={a.v3_id} onClick={() => navigate(`/apply?id=${a.v3_id}`)}
              className="w-full text-left flex items-start gap-2 border border-amber-300 bg-amber-50 rounded px-3 py-2.5 hover:bg-amber-100" data-testid="info-required-alert">
              <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
              <span className="text-[12px] text-amber-800">
                <b>{a.applicant_no} · {a.company_Name}</b> needs more information: {a.info_request?.message || "see file"} — tap to resume
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 mb-6" data-testid="console-tiles">
        {TILES.map((t) => (
          <button key={t.key} onClick={() => setFilter(filter === t.key ? "" : t.key)} data-testid={`tile-${t.key}`}
            className={`border rounded-sm px-2 py-2.5 text-center transition-colors ${filter === t.key ? "border-[#b3122a] bg-[#b3122a]/5" : "border-slate-200 bg-white hover:border-slate-400"}`}>
            <div className="text-xl font-bold tabular-nums text-slate-900">{apps ? count(t.stages) : "…"}</div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{t.label}</div>
          </button>
        ))}
        <div className="border border-dashed border-slate-200 rounded-sm px-2 py-2.5 text-center" data-testid="tile-commission">
          <div className="text-xl font-bold text-slate-300">—</div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Commission</div>
          <div className="text-[9px] text-slate-300">Phase 2</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm" data-testid="console-app-list">
          <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-2">
            <span className="font-semibold text-slate-900 text-sm">My applications</span>
            <SourceBadge source="TEST_V3" />
            {filter && <button onClick={() => setFilter("")} className="ml-auto text-[11px] text-[#b3122a] font-bold" data-testid="clear-filter-btn">Clear filter ×</button>}
          </div>
          {apps === null ? <LoadingState /> : visible.length === 0 ? (
            <EmptyState title="No applications" detail="Create your first test application — historical files stay read-only." />
          ) : (
            <div className="divide-y divide-slate-100 max-h-[480px] overflow-auto">
              {visible.map((a) => (
                <button key={a.v3_id} onClick={() => navigate(`/apply?id=${a.v3_id}`)} data-testid="console-app-row"
                  className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 truncate">{a.company_Name}</div>
                    <div className="text-[11px] text-slate-400">{a.applicant_no} · {a.applied_loan_amt ? formatCr(a.applied_loan_amt) : "—"} · updated {new Date(a.updated_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden hidden sm:block">
                      <div className="h-full bg-[#b3122a]" style={{ width: `${a.completeness || 0}%` }} />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: STAGE_META[a.stage]?.color }}>{STAGE_META[a.stage]?.label || a.stage}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="quality-score-card">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-slate-900 text-sm">DSA Quality Score</span>
              <SourceBadge source="TEST_V3" />
            </div>
            {!score ? <LoadingState /> : !score.available ? (
              <div className="text-[12px] text-slate-400 mt-2" data-testid="quality-score-empty">{score.note}</div>
            ) : (
              <div data-testid="quality-score-value">
                <div className="ceo-fig text-4xl text-[#111111] mt-1">{score.score}<span className="text-lg text-slate-400">/100</span></div>
                <div className="mt-3 space-y-1.5 text-[12px]">
                  {[["Completeness", score.components.avg_completeness], ["Conversion", score.components.conversion_pct], ["Documentation", score.components.documentation_pct]].map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="w-24 text-slate-400">{k}</span>
                      <span className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><span className="block h-full bg-[#111111]" style={{ width: `${v}%` }} /></span>
                      <span className="tabular-nums font-bold text-slate-700 w-8 text-right">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-400 mt-2">{score.note}</div>
              </div>
            )}
          </div>
          <LegacyLookup />
        </div>
      </div>
    </div>
  );
}
